import { say } from "../../words.ts";
import type { ChatEvent, ChatFeed, ChatSend } from "../chat/events.ts";
import type { PartyStanding } from "../chat/commands.ts";
import { PAGE_TIMERS, type LookupTimers } from "../chat/lookup.ts";
import type { PartyVitalsRecord } from "odyssey-shared";
import { sameName, type PartyMember, type PartyName, type PartyRequest } from "./members.ts";
import { partyNoticeLine, partyRefusalSentence, type RefusalContext } from "./refusal-words.ts";
import { NO_VITALS, type MemberVitals } from "./vitals.ts";
import { contextOf, sameRoster } from "./store-helpers.ts";
import { VitalsBook } from "./vitals-book.ts";

/**
 * The page's party (ADR-101 decisions 3 to 9, 14, 19 and 23, and its note of 2026-10-15): the roster the gateway
 * sends whole at every change, the invitations waiting for an answer, one shown at a time with the rest queued, the
 * menu a player opened, and the system lines the party's notices and refusals make. It holds what the gateway says
 * and never decides: every request goes out as a message and every change comes back as a roster or a notice.
 *
 * An invitation is non-modal. It takes no focus and blocks nothing, and it is not shown while the player is in
 * combat: the shown one closes the moment combat starts, the others wait silently, and each lapses at its own time, so
 * one still open when the fight ends is shown again.
 */

/**
 * How long a refusal is taken to answer the request before it, in milliseconds. The gateway's `partyRefused` names a
 * reason and not the request, and requests are answered in order, so a refusal later than this belongs to none the
 * page remembers.
 */
export const REFUSAL_WINDOW_MS = 10_000;

/**
 * The most invitations the page holds at once: the gateway bounds a character's pending invitations (decision 29),
 * and a page past it holds the oldest.
 */
const MAX_PROMPTS = 8;

/**
 * The roster of no party, one array for every snapshot that holds none.
 */
const NO_MEMBERS: readonly PartyMember[] = [];

/**
 * An invitation waiting for an answer: its id, the inviter's names, how many members the party seats, when it lapses
 * on the page's clock and how long it was offered, whether an Accept is waiting on the gateway, and the party's flag (0
 * Protected, 1 Flag on; the founder's preference for a party of one).
 */
export interface PartyPrompt {
  readonly invite: number;
  readonly firstName: string;
  readonly familyName: string;
  readonly members: number;
  readonly deadline: number;
  readonly total: number;
  readonly answering: boolean;
  readonly flag: number;
}

/**
 * Where the player menu opens and for whom: the character's names and a point in client CSS pixels.
 */
export interface PartyMenuAt extends PartyName {
  readonly x: number;
  readonly y: number;
}

/**
 * What the party's surfaces show: the roster (empty unless the party seats two or more), the player's own seat, the
 * invitation shown (null while none waits or the player is in combat) and how many more wait, and the menu. `vitals`
 * holds the vitals of the members a frame of `partyVitals` last named and the player's own, by roster place.
 */
export interface PartySnapshot {
  readonly members: readonly PartyMember[];
  readonly self: PartyMember | null;
  readonly vitals: ReadonlyMap<number, MemberVitals>;
  readonly prompt: PartyPrompt | null;
  readonly waiting: number;
  readonly menu: PartyMenuAt | null;
  /** Whether invitations are held back, as `setInCombat` was last told: the player is in combat or dead. */
  readonly held: boolean;
}

/**
 * What the surfaces read: the snapshot, a way to hear that it changed, and the actions the player takes.
 */
export interface PartySource {
  readonly subscribe: (listener: () => void) => () => void;
  readonly getSnapshot: () => PartySnapshot;
  readonly now: () => number;
  readonly request: (request: PartyRequest) => boolean;
  readonly answer: (invite: number, accept: boolean) => boolean;
  readonly openMenu: (at: PartyMenuAt) => void;
  readonly closeMenu: () => void;
}

/**
 * The store's clock, the line it writes to the chat log, the player's own names, and the timers its invitations
 * lapse on.
 */
export interface PartyStoreOptions {
  readonly now?: () => number;
  readonly line?: (text: string) => void;
  readonly self?: () => PartyName | null;
  readonly timers?: LookupTimers;
}

/**
 * Whether invitations are held back: while the player is in combat or dead.
 *
 * @param vitals The player's vitals, or null before they are known.
 * @returns True when no invitation shows.
 */
export function invitationsHeld(vitals: { readonly inCombat: boolean; readonly dead: boolean } | null | undefined): boolean {
  return (vitals?.inCombat ?? false) || (vitals?.dead ?? false);
}

/**
 * The party store.
 *
 * @example
 * const party = new PartyStore({ line: (text) => chat.note(text), self: () => gameReport.name });
 * party.bind((request) => connection.sendChat(request));
 * party.receive({ kind: "partyInvited", invite: 7, firstName: "Vessa", familyName: "Thorn", members: 3, expiresMs: 30_000 });
 */
export class PartyStore implements PartySource, Pick<ChatFeed, "bind" | "receive"> {
  private snapshot: PartySnapshot = { members: NO_MEMBERS, self: null, vitals: NO_VITALS, prompt: null, waiting: 0, menu: null, held: false };
  private readonly listeners = new Set<() => void>();
  private readonly timers: LookupTimers;
  private send: ChatSend = () => false;
  private roster: readonly PartyMember[] = [];
  private prompts: readonly PartyPrompt[] = [];
  private combat = false;
  private readonly vitals = new VitalsBook();
  private heldAtEnd = false;
  private awaitingJoin = true;
  private leaving = false;
  private lapse: unknown = null;
  private asked: { readonly request: NonNullable<RefusalContext["request"]>; readonly name: string | null; readonly invite: number; readonly at: number } | null = null;

  /**
   * @param options The clock, the chat log's line, the player's names and the timers.
   */
  public constructor(private readonly options: PartyStoreOptions = {}) {
    this.timers = options.timers ?? PAGE_TIMERS;
  }

  /**
   * The page's clock.
   *
   * @returns Milliseconds.
   */
  public readonly now = (): number => (this.options.now ?? Date.now)();

  /**
   * Listens for changes.
   *
   * @param listener Called after each change.
   * @returns A function that stops listening.
   */
  public readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  /**
   * What the surfaces show now.
   *
   * @returns The snapshot, the same object until something changes.
   */
  public readonly getSnapshot = (): PartySnapshot => this.snapshot;

  /**
   * Gives the store the way to send on whatever connection the scene holds.
   *
   * @param send The sender.
   */
  public bind(send: ChatSend): void {
    this.send = send;
  }

  /**
   * Takes a `partyVitals` frame: the vitals of the members the map server holds on the player's map. A member the
   * frame leaves out is no longer drawn with health, and a frame of none clears them all.
   *
   * @param record The decoded frame.
   */
  public receiveVitals(record: Readonly<PartyVitalsRecord>): void {
    if (this.vitals.receive(record)) {
      this.change(this.view({}));
    }
  }

  /**
   * Sets the player's own vitals, which the frame of `partyVitals` never carries, from the HUD's.
   *
   * @param own The player's vitals, or null before the first `vitals`.
   */
  public setOwnVitals(own: MemberVitals | null): void {
    if (this.vitals.setOwn(own)) {
      this.change(this.view({}));
    }
  }

  /**
   * Tells the store whether the player is in combat (the vitals' bit). Entering combat closes the shown invitation
   * and keeps the others waiting silently; leaving it shows the first one still open.
   *
   * @param inCombat Whether the player is in combat.
   */
  public setInCombat(inCombat: boolean): void {
    if (this.combat === inCombat) {
      return;
    }
    this.combat = inCombat;
    this.refresh();
  }

  /**
   * Takes a frame the connection read, or its end.
   *
   * @param event The event; the ones that are not the party's are ignored.
   */
  public receive(event: ChatEvent): void {
    switch (event.kind) {
      case "party":
        this.rosterHeld(event.members);
        return;
      case "partyInvited":
        this.invited(event);
        return;
      case "partyNotice":
        this.noticed(event);
        return;
      case "partyRefused":
        this.refused(event.reason, event.waitMs);
        return;
      case "ended":
        this.connectionEnded();
        return;
      default:
        return;
    }
  }

  /**
   * Sends a request, remembering it so a refusal can name the player it was about.
   *
   * @param request The request.
   * @returns False when there is no connection ready, after saying so in the log.
   */
  public readonly request = (request: PartyRequest): boolean => {
    if (!this.send(request)) {
      this.say(say("chat.notConnected"));
      return false;
    }
    const kind = contextOf(request);
    const name = "firstName" in request ? `${request.firstName} ${request.familyName}`.trim() : null;
    this.asked = { request: kind, name, invite: request.kind === "partyAnswer" ? request.invite : 0, at: this.now() };
    this.leaving = request.kind === "partyLeave";
    return true;
  };

  /**
   * Answers an invitation. A decline closes its prompt at once; an accept leaves it open, marked as waiting, until the
   * roster arrives or the gateway refuses (decision 5: a refusal leaves the invitation pending).
   *
   * @param invite The invitation's id.
   * @param accept True to accept.
   * @returns False when it was not sent.
   */
  public readonly answer = (invite: number, accept: boolean): boolean => {
    const held = this.prompts.find((prompt) => prompt.invite === invite);
    if (held === undefined || held.answering || !this.request({ kind: "partyAnswer", invite, accept })) {
      return false;
    }
    this.prompts = accept ? this.prompts.map((prompt) => (prompt.invite === invite ? { ...prompt, answering: true } : prompt)) : this.prompts.filter((prompt) => prompt.invite !== invite);
    this.refresh();
    return true;
  };

  /**
   * Opens the player menu for a character.
   *
   * @param at The character and the point it opens at.
   */
  public readonly openMenu = (at: PartyMenuAt): void => {
    this.change({ ...this.snapshot, menu: at });
  };

  /**
   * Closes the player menu.
   */
  public readonly closeMenu = (): void => {
    if (this.snapshot.menu !== null) {
      this.change({ ...this.snapshot, menu: null });
    }
  };

  /**
   * Lets the invitations whose time has passed lapse: each is dropped, and said in the log unless the player is in
   * combat, where nothing is said. The store schedules this itself; a test calls it after moving the clock.
   */
  public expire(): void {
    const now = this.now();
    const lapsed = this.prompts.filter((prompt) => prompt.deadline <= now);
    if (lapsed.length === 0) {
      this.schedule();
      return;
    }
    this.prompts = this.prompts.filter((prompt) => prompt.deadline > now);
    if (!this.combat) {
      for (const prompt of lapsed) {
        this.say(say("party.prompt.expired", { name: `${prompt.firstName} ${prompt.familyName}` }));
      }
    }
    this.refresh();
  }

  /**
   * Takes the roster. The first one after a join is the join's: it says the party ended when the page held one before
   * the connection ended and the join's is empty (decision 14). Seated, the page drops its invitations, since the
   * gateway closes the others when one is accepted.
   *
   * @param members The roster as sent.
   */
  private rosterHeld(members: readonly PartyMember[]): void {
    const sorted = [...members].sort((a, b) => a.place - b.place);
    const before = this.roster;
    const next = sameRoster(before, sorted) ? before : sorted;
    this.roster = next;
    if (this.awaitingJoin) {
      this.awaitingJoin = false;
      if (next.length === 0 && this.heldAtEnd) {
        this.say(say("party.notice.endedAtJoin"));
      }
      this.heldAtEnd = false;
    } else if (next.length === 0 && before.length > 0 && this.leaving) {
      this.say(say("party.notice.leftSelf"));
    }
    if (next.length === 0) {
      this.leaving = false;
    }
    if (this.seated(next) !== null) {
      this.prompts = [];
    }
    this.vitals.keepSeated(next);
    this.change(this.view({ menu: null }));
    this.schedule();
  }

  /**
   * Takes an invitation: queued behind any already waiting, and not shown while the player is in combat.
   *
   * @param event The invitation.
   */
  private invited(event: Extract<ChatEvent, { kind: "partyInvited" }>): void {
    if (this.prompts.some((prompt) => prompt.invite === event.invite)) {
      return;
    }
    const prompt: PartyPrompt = { invite: event.invite, firstName: event.firstName, familyName: event.familyName, members: event.members, deadline: this.now() + event.expiresMs, total: event.expiresMs, answering: false, flag: event.flag };
    this.prompts = [...this.prompts, prompt].slice(-MAX_PROMPTS);
    this.refresh();
  }

  /**
   * Takes a notice: its system line, and a withdrawn invitation's prompt closed.
   *
   * @param event The notice.
   */
  private noticed(event: Extract<ChatEvent, { kind: "partyNotice" }>): void {
    if (event.notice === "withdrawn") {
      const held = this.prompts.some((prompt) => prompt.invite === event.invite);
      this.prompts = this.prompts.filter((prompt) => prompt.invite !== event.invite);
      if (held) {
        this.say(partyNoticeLine("withdrawn", event, null));
        this.refresh();
      }
      return;
    }
    this.say(partyNoticeLine(event.notice, event, this.options.self?.() ?? null));
  }

  /**
   * Takes a refusal: its system line, worded with the request it most likely answers, and an accept's waiting ended.
   * An invitation the gateway no longer holds is dropped.
   *
   * @param reason The reason as sent.
   * @param waitMs The wait it carried.
   */
  private refused(reason: string, waitMs: number): void {
    const asked = this.asked !== null && this.now() - this.asked.at <= REFUSAL_WINDOW_MS ? this.asked : null;
    this.say(partyRefusalSentence(reason, waitMs, { request: asked?.request ?? null, name: asked?.name ?? null }));
    if (asked?.request === "answer") {
      this.prompts = reason === "gone" ? this.prompts.filter((prompt) => prompt.invite !== asked.invite) : this.prompts.map((prompt) => (prompt.invite === asked.invite ? { ...prompt, answering: false } : prompt));
      this.refresh();
    }
    this.leaving = false;
  }

  /**
   * Takes the connection's end: the roster and the invitations are cleared, and whether a party was held is kept for
   * the next join to word.
   */
  private connectionEnded(): void {
    this.heldAtEnd = this.heldAtEnd || this.roster.length >= 2;
    this.awaitingJoin = true;
    this.roster = [];
    this.vitals.clear();
    this.prompts = [];
    this.leaving = false;
    this.asked = null;
    this.change(this.view({ menu: null }));
    this.schedule();
  }

  /**
   * Forgets the character the page played, as Change character must before the next joins: the roster and the
   * invitations go, and the next join's empty roster says nothing of a party that was another character's.
   */
  public forget(): void {
    this.connectionEnded();
    this.heldAtEnd = false;
  }

  /**
   * Where the player stands in the party shown: none while no party shows or the player's own seat is not known,
   * leader when the seat leads, member otherwise.
   *
   * @returns The standing.
   */
  public readonly standing = (): PartyStanding => {
    const self = this.snapshot.self;
    return self === null ? "none" : self.leader ? "leader" : "member";
  };

  /**
   * The player's own seat in a roster.
   *
   * @param members The roster.
   * @returns The seat, or null when the roster does not hold the player.
   */
  private seated(members: readonly PartyMember[]): PartyMember | null {
    const self = this.options.self?.() ?? null;
    return self === null ? null : (members.find((member) => sameName(member, self)) ?? null);
  }

  /**
   * Builds the snapshot from what the store holds. A roster under two seats is no party and is not shown.
   *
   * @param over Fields to replace.
   * @returns The snapshot.
   */
  private view(over: Partial<PartySnapshot>): PartySnapshot {
    const members = this.roster.length >= 2 ? this.roster : NO_MEMBERS;
    const open = this.prompts.filter((prompt) => prompt.deadline > this.now());
    const shown = this.combat ? null : (open[0] ?? null);
    const self = members.length === 0 ? null : this.seated(members);
    return { members, self, vitals: this.vitals.forSeat(self), prompt: shown, waiting: this.combat ? 0 : Math.max(0, open.length - 1), menu: this.snapshot.menu, held: this.combat, ...over };
  }

  /**
   * Rebuilds the snapshot after the invitations or the combat state changed, and reschedules the lapse.
   */
  private refresh(): void {
    this.change(this.view({}));
    this.schedule();
  }

  /**
   * Schedules the next lapse at the earliest deadline held.
   */
  private schedule(): void {
    if (this.lapse !== null) {
      this.timers.clear(this.lapse);
      this.lapse = null;
    }
    if (this.prompts.length === 0) {
      return;
    }
    const next = Math.min(...this.prompts.map((prompt) => prompt.deadline));
    this.lapse = this.timers.set(() => {
      this.lapse = null;
      this.expire();
    }, Math.max(0, next - this.now()));
  }

  /**
   * Writes a line to the chat log.
   *
   * @param text The line, or null for none.
   */
  private say(text: string | null): void {
    if (text !== null) {
      this.options.line?.(text);
    }
  }

  /**
   * Replaces the snapshot and tells the listeners when it differs.
   *
   * @param next The new snapshot.
   */
  private change(next: PartySnapshot): void {
    const last = this.snapshot;
    if (next.members === last.members && next.self === last.self && next.vitals === last.vitals && next.prompt === last.prompt && next.waiting === last.waiting && next.menu === last.menu && next.held === last.held) {
      return;
    }
    this.snapshot = next;
    for (const listener of this.listeners) {
      listener();
    }
  }
}
